// /src/backendServices/fileManagment.api.ts

import axios from 'axios';
import { UploadConfig, UploadCallbacks, UploadMetadata } from '../../dataTypes/fileManagment/types';

// Configuration par défaut
const DEFAULT_CHUNK_SIZE = 10 * 1024 * 1024; // 10 Mo
const DEFAULT_MAX_RETRIES = 3;

// Classe générique pour l'upload de gros fichiers
export class GenericFileUploader {
  private config: Required<UploadConfig>;
  private callbacks: UploadCallbacks;

  constructor(config: UploadConfig, callbacks: UploadCallbacks = {}) {
    this.config = {
      chunkSize: config.chunkSize || DEFAULT_CHUNK_SIZE,
      maxRetries: config.maxRetries || DEFAULT_MAX_RETRIES,
      uploadChunkUrl: config.uploadChunkUrl,
      finalizeUploadUrl: config.finalizeUploadUrl,
      enableResume: config.enableResume ?? true,
    };
    this.callbacks = callbacks;
  }

  // Clé pour sauvegarder l'état de l'upload
  private getUploadStateKey(fileId: string): string {
    return `file-upload-${fileId}`;
  }

  // Sauvegarde du dernier chunk uploadé (si résumption activée)
  private saveLastUploadedChunk(fileId: string, chunkIndex: number): void {
    if (this.config.enableResume && typeof localStorage !== 'undefined') {
      localStorage.setItem(this.getUploadStateKey(fileId), chunkIndex.toString());
    }
  }

  // Récupération du dernier chunk uploadé
  private getLastUploadedChunk(fileId: string): number {
    if (!this.config.enableResume || typeof localStorage === 'undefined') {
      return 0;
    }
    
    const stored = localStorage.getItem(this.getUploadStateKey(fileId));
    return stored ? parseInt(stored, 10) : 0;
  }

  // Nettoyage de l'état d'upload
  private clearUploadState(fileId: string): void {
    if (this.config.enableResume && typeof localStorage !== 'undefined') {
      localStorage.removeItem(this.getUploadStateKey(fileId));
    }
  }

  // Upload d'un chunk avec retry automatique
  private async uploadChunkWithRetry(
    formData: FormData, 
    chunkIndex: number, 
    attempt = 1
  ): Promise<void> {
    try {
      await axios.post(this.config.uploadChunkUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
    } catch (error) {
		
      console.log (error)		
		
      if (attempt < this.config.maxRetries) {
        // Délai exponentiel: 1s, 2s, 4s...
        const delay = 1000 * Math.pow(2, attempt - 1);
        await new Promise((resolve) => setTimeout(resolve, delay));
        return this.uploadChunkWithRetry(formData, chunkIndex, attempt + 1);
      } else {
        const uploadError = error instanceof Error ? error : new Error(`Upload failed for chunk ${chunkIndex}`);
        this.callbacks.onError?.(uploadError, chunkIndex);
        throw uploadError;
      }
    }
  }

  // Méthode principale d'upload
  async uploadFile(
    file: File, 
    fileId: string, 
    metadata: UploadMetadata = {}
  ): Promise<void> {
    try {
      const totalChunks = Math.ceil(file.size / this.config.chunkSize);
      const lastUploaded = this.getLastUploadedChunk(fileId);

      // Upload des chunks
      for (let i = lastUploaded; i < totalChunks; i++) {
        const start = i * this.config.chunkSize;
        const end = Math.min(file.size, start + this.config.chunkSize);
        const chunk = file.slice(start, end);

        const formData = new FormData();
        formData.append('chunk', chunk);
        formData.append('fileId', fileId);
        formData.append('chunkIndex', i.toString());
        formData.append('totalChunks', totalChunks.toString());
        formData.append('fileName', file.name);
        formData.append('fileType', file.type || 'application/octet-stream');
        formData.append('totalSize', file.size.toString());

        // Ajout des métadonnées personnalisées
        Object.entries(metadata).forEach(([key, value]) => {
          formData.append(key, value.toString());
        });

        await this.uploadChunkWithRetry(formData, i);

        // Sauvegarde de l'état
        this.saveLastUploadedChunk(fileId, i + 1);

        // Callbacks de progression
        const progressPercent = Math.round(((i + 1) / totalChunks) * 100);
        this.callbacks.onProgress?.(progressPercent);
        this.callbacks.onChunkUploaded?.(i, totalChunks);
      }

      // Finalisation de l'upload. Le nom d'origine du fichier voyage avec CHAQUE
      // morceau mais se perdait ici : c'est le finalize qui écrit la fiche, donc
      // c'est lui qui doit le connaître pour le ranger dans `originalFile`.
      await this.finalizeUpload(fileId, metadata, file.name);
      
      // Nettoyage
      this.clearUploadState(fileId);
      this.callbacks.onComplete?.();

    } catch (error) {
      const uploadError = error instanceof Error ? error : new Error('Upload failed');
      this.callbacks.onError?.(uploadError);
      throw uploadError;
    }
  }

  // Finalisation de l'upload
  private async finalizeUpload(fileId: string, metadata: UploadMetadata, fileName?: string): Promise<void> {
    const finalizeData = {
      fileId,
      fileName,       // le nom d'origine ; un metadata.fileName explicite reste prioritaire
      ...metadata,
    };


    console.log ('finalizeUpload', finalizeData)
    await axios.post(this.config.finalizeUploadUrl, finalizeData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  // Méthode pour annuler un upload en cours
  cancelUpload(fileId: string): void {
    this.clearUploadState(fileId);
  }

  // Méthode statique pour créer une instance avec configuration par défaut
  static create(
    uploadChunkUrl: string, 
    finalizeUploadUrl: string, 
    options: Partial<UploadConfig> = {}
  ): GenericFileUploader {
    return new GenericFileUploader({
      uploadChunkUrl,
      finalizeUploadUrl,
      ...options,
    });
  }
}

/**
 * Efface l'état de reprise laissé dans localStorage par un upload.
 *
 * Un upload qui va au bout nettoie sa clé lui-même. Un upload qui échoue, lui, la laisse
 * derrière lui : sans ce nettoyage, `file-upload-<id>` s'accumule indéfiniment. C'est ce
 * qu'appelle la suppression d'une ligne dans le gestionnaire d'uploads, pour que retirer
 * l'entrée de l'affichage retire aussi sa trace.
 */
export const clearUploadResumeState = (fileId: string): void => {
  if (typeof localStorage !== 'undefined') {
    localStorage.removeItem(`file-upload-${fileId}`);
  }
};

// Fonction utilitaire pour upload simple (compatible avec l'ancienne API)
export const uploadLargeFile = async (
  file: File,
  fileId: string,
  config: UploadConfig,
  callbacks: UploadCallbacks = {}
): Promise<void> => {
  const uploader = new GenericFileUploader(config, callbacks);
  return uploader.uploadFile(file, fileId);
};

// Export des types pour faciliter l'utilisation
export type { UploadConfig, UploadCallbacks, UploadMetadata };